import { useEffect } from 'react';
import { resolveOverlayHost, toHostCoords } from '../utils/overlayHost';

/**
 * "Drag it there" demonstration for a step with a `dragDemo` config (today
 * the ranking mark, Figma 9430-3293): a ghost copy of the card to drag lifts
 * off, travels along an arc to the slot it would take when dropped right
 * before the hinted card, hovers there, flies back home and fades, so the
 * user sees the gesture and then makes it themselves (or presses Next).
 *
 * The real card never moves: it only dims while the ghost is out, so the
 * highlight outline the runner measures around it stays put. The ghost is
 * portaled where the coach marks are (`utils/overlayHost.js`), above the
 * bubble, and in the page scroller's coordinates so it scrolls with the
 * cards.
 *
 * Plays once each time the mark appears. Skipped when the card already sits
 * where the demo would put it (nothing left to show), under
 * `prefers-reduced-motion`, and where the Web Animations API is missing.
 * Cancelled the moment the user takes over the cards (pointer, drag or key)
 * or the mark goes away.
 *
 * `dragDemo` is `{ item, before, slots }`, three selectors resolved inside
 * the step's anchor: the card to drag, the card it must end up right before,
 * and every card slot (in order) so the landing slot can be worked out.
 */

/** Phase lengths (ms) and the arc height (px): a pace a first-time viewer can follow. */
export const DRAG_DEMO_TIMING = Object.freeze({
    pickUpMs: 400,
    travelMs: 1400,
    hoverMs: 700,
    returnMs: 1100,
    settleMs: 400,
    liftPx: 48,
});
export const DRAG_DEMO_TOTAL_MS =
    DRAG_DEMO_TIMING.pickUpMs +
    DRAG_DEMO_TIMING.travelMs +
    DRAG_DEMO_TIMING.hoverMs +
    DRAG_DEMO_TIMING.returnMs +
    DRAG_DEMO_TIMING.settleMs;
export const DRAG_DEMO_GHOST_CLASS = 'walkthrough-drag-demo';
export const DRAG_DEMO_TEST_ID = 'walkthrough-drag-demo';

/** The user taking the cards over, caught on the anchor in the capture phase. */
const TAKE_OVER_EVENTS = ['pointerdown', 'mousedown', 'touchstart', 'dragstart', 'keydown'];
/** Opacity of the real card while its ghost is out. */
const DIMMED_OPACITY = 0.35;
const LIFTED_SCALE = 1.04;
const LIFTED_SHADOW = '0 12px 28px rgba(16, 24, 40, 0.22)';
const ARC_SAMPLES = 24;

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

export const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Work out what to animate: the card to drag and where its ghost lands (the
 * slot the card would take when dropped right before `before`). `null` when
 * the cards are missing or the card already sits there.
 */
export function planDragDemo(container, { item, before, slots }) {
    if (!container) return null;
    const slotElements = Array.from(container.querySelectorAll(slots));
    const itemElement = container.querySelector(item);
    const beforeElement = container.querySelector(before);
    if (!itemElement || !beforeElement) return null;
    const from = slotElements.indexOf(itemElement);
    const target = slotElements.indexOf(beforeElement);
    if (from === -1 || target === -1) return null;
    // Dragging rightwards lands on the slot just left of the target; leftwards, on the target's own slot.
    const landing = from < target ? target - 1 : target;
    if (landing === from) return null;
    return { item: itemElement, landing: slotElements[landing] };
}

/** Arc from `(0, 0)` to `(dx, dy)`, bulging `lift` px upwards, as evenly timed keyframes. */
const arcKeyframes = ({ dx, dy, lift, startOffset, endOffset, reverse = false }) => {
    const frames = [];
    for (let i = 0; i <= ARC_SAMPLES; i += 1) {
        const progress = i / ARC_SAMPLES;
        const t = easeInOutCubic(reverse ? 1 - progress : progress);
        const x = dx * t;
        const y = dy * t - lift * 4 * t * (1 - t);
        frames.push({
            offset: startOffset + (endOffset - startOffset) * progress,
            transform: `translate(${x.toFixed(2)}px, ${(y - 6).toFixed(2)}px) scale(${LIFTED_SCALE})`,
            opacity: 1,
            boxShadow: LIFTED_SHADOW,
            easing: 'linear',
        });
    }
    return frames;
};

/** Keyframes of the ghost's whole trip, offsets as fractions of `DRAG_DEMO_TOTAL_MS`. */
export function ghostKeyframes({ dx, dy }, timing = DRAG_DEMO_TIMING) {
    const total = DRAG_DEMO_TOTAL_MS;
    const pickUpEnd = timing.pickUpMs / total;
    const travelEnd = pickUpEnd + timing.travelMs / total;
    const hoverEnd = travelEnd + timing.hoverMs / total;
    const returnEnd = hoverEnd + timing.returnMs / total;
    const out = arcKeyframes({ dx, dy, lift: timing.liftPx, startOffset: pickUpEnd, endOffset: travelEnd });
    // The return starts where the hover ends: its first sample *is* the landed frame.
    const back = arcKeyframes({ dx, dy, lift: timing.liftPx, startOffset: hoverEnd, endOffset: returnEnd, reverse: true });
    return [
        { offset: 0, transform: 'translate(0px, 0px) scale(1)', opacity: 0, boxShadow: 'none', easing: 'ease-in-out' },
        ...out,
        ...back,
        { offset: 1, transform: 'translate(0px, 0px) scale(1)', opacity: 0, boxShadow: 'none' },
    ];
}

/** The real card dims while its ghost is away and comes back as the ghost fades. */
export function itemKeyframes(timing = DRAG_DEMO_TIMING) {
    const total = DRAG_DEMO_TOTAL_MS;
    const pickUpEnd = timing.pickUpMs / total;
    const settleStart = 1 - timing.settleMs / total;
    return [
        { offset: 0, opacity: 1 },
        { offset: pickUpEnd, opacity: DIMMED_OPACITY },
        { offset: settleStart, opacity: DIMMED_OPACITY },
        { offset: 1, opacity: 1 },
    ];
}

/** A look-alike of `item` that carries none of its behaviour or semantics. */
const makeGhost = (item) => {
    const ghost = item.cloneNode(true);
    ['draggable', 'tabindex', 'role', 'aria-label', 'data-pillar', 'data-walkthrough', 'id'].forEach((attr) =>
        ghost.removeAttribute(attr)
    );
    ghost.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    ghost.setAttribute('aria-hidden', 'true');
    ghost.setAttribute('data-testid', DRAG_DEMO_TEST_ID);
    ghost.classList.add(DRAG_DEMO_GHOST_CLASS);
    return ghost;
};

/**
 * Play the demonstration for `plan`. Returns a `stop()` that cancels it and
 * removes the ghost; the ghost also removes itself once the trip is over.
 */
export function playDragDemo({ item, landing }) {
    if (typeof item.animate !== 'function') return () => {};
    const fromRect = item.getBoundingClientRect();
    const toRect = landing.getBoundingClientRect();
    const { mode, host } = resolveOverlayHost(item);
    const native = mode === 'native';
    const origin = native ? toHostCoords({ top: fromRect.top, left: fromRect.left }, host) : { top: fromRect.top, left: fromRect.left };

    const ghost = makeGhost(item);
    Object.assign(ghost.style, {
        position: native ? 'absolute' : 'fixed',
        top: `${origin.top}px`,
        left: `${origin.left}px`,
        width: `${fromRect.width}px`,
        height: `${fromRect.height}px`,
        maxWidth: 'none',
        margin: '0',
        pointerEvents: 'none',
        opacity: '0',
        transformOrigin: 'center',
        willChange: 'transform, opacity',
    });
    host.appendChild(ghost);

    const options = { duration: DRAG_DEMO_TOTAL_MS, fill: 'none' };
    const ghostAnimation = ghost.animate(ghostKeyframes({ dx: toRect.left - fromRect.left, dy: toRect.top - fromRect.top }), options);
    const itemAnimation = item.animate(itemKeyframes(), options);

    let stopped = false;
    const stop = () => {
        if (stopped) return;
        stopped = true;
        ghostAnimation.cancel();
        itemAnimation.cancel();
        ghost.remove();
    };
    ghostAnimation.finished.then(stop, () => {});
    return stop;
}

/**
 * Run the demonstration of `demo` inside `element` while `enabled`, once per
 * appearance. See the module doc.
 */
export function useDragDemo({ enabled, element, demo }) {
    useEffect(() => {
        if (!enabled || !element || !demo || prefersReducedMotion()) return undefined;
        const plan = planDragDemo(element, demo);
        if (!plan) return undefined;
        const stop = playDragDemo(plan);
        TAKE_OVER_EVENTS.forEach((type) => element.addEventListener(type, stop, true));
        return () => {
            stop();
            TAKE_OVER_EVENTS.forEach((type) => element.removeEventListener(type, stop, true));
        };
    }, [enabled, element, demo]);
}

export default useDragDemo;
